/**
 * 掲示板のアプリ内バッジ用フック (v3.5 Board / Phase 2)。
 * 仕様書: docs/02_work/done/plan_board_feature.md §6-3（宛先側 + 投稿者側の2本立て）
 *
 * 2 本の onSnapshot を張り件数を導出する:
 * 1. 宛先側:   where('openRecipientUids','array-contains', uid)
 *              → 未確認数 / 未対応数 / 期日超過数（トリアージストリップと共用）
 * 2. 投稿者側: where('authorUid','==',uid).where('authorUnseenActivity','==',true)
 *              → 「自分の投稿に新しいアクションあり」件数
 *
 * サイドバーバッジ:
 *   - 投稿バッジ   = 未対応数 + 投稿者側件数 (badgeTotal)
 *   - スレッドバッジ = 参加者として関わる投稿のスレッド新着件数 (threadNews)
 *     ※ スレッド投稿は threadCount しか触らない（updatedAt も openRecipientUids も
 *        変えない = 無限ループ防止の設計）ため、別途 threadCount - threadSeen[me]
 *        で数える必要がある。一覧の 💬 バッジと同じ基準。
 *
 * 購読ゲート: ログイン済み && 内部メンバー (level !== 'external') && board.posts の view 権限。
 * ゲートを通らない場合は購読せずゼロを返す（rules が read を拒否するため）。
 *
 * 家訓 (feedback_firestore_loops): onSnapshot コールバック内は setState のみ。
 * Firestore への書き戻しは絶対にしない。
 */
import type { BoardPost } from '@goenhub/shared';
import { BOARD_COLLECTIONS } from '@goenhub/shared';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { usePermission } from '@/features/permissions';
import { useAuth } from '@/hooks/useAuth';
import { db } from '@/lib/firebase';
import { todayYmd } from '@/pages/board/boardMeta';

/** 宛先側購読 1 件分の最小スナップショット。 */
interface AttentionDoc {
  id: string;
  dueDate: string | null;
  /** recipientStates[自分uid].state（欠損時 null）。 */
  myState: string | null;
  /** スレッド新着数 = threadCount - threadSeen[自分]（0 未満は 0）。 */
  threadUnread: number;
}

/** 投稿者側スナップショット 1 件分（スレッド新着の算出に使う）。 */
interface AuthorDoc {
  id: string;
  unseenActivity: boolean;
  threadUnread: number;
}

export interface BoardAttention {
  /** 自分宛で未確認 (state=unread) の投稿数。 */
  unread: number;
  /** 自分宛で未対応 (自分 ∈ openRecipientUids) の投稿数。 */
  open: number;
  /** 未対応のうち期日超過の投稿数。 */
  overdue: number;
  /** 自分の投稿に新しいアクションがある投稿数 (authorUnseenActivity)。 */
  authorNews: number;
  /** 投稿バッジ = open + authorNews。 */
  badgeTotal: number;
  /** スレッド新着の合計件数（自分宛 + 自分の投稿。重複は除く）。 */
  threadNews: number;
  /** 自分宛で未対応の postId 集合（一覧のトリアージフィルタ用）。 */
  minePostIds: Set<string>;
}

const ZERO: BoardAttention = {
  unread: 0,
  open: 0,
  overdue: 0,
  authorNews: 0,
  badgeTotal: 0,
  threadNews: 0,
  minePostIds: new Set<string>(),
};

export function useBoardAttention(): BoardAttention {
  const { user } = useAuth();
  const canView = usePermission('board.posts', 'view');
  const uid = user?.uid ?? null;
  // 外部SA (level==='external') と権限なしユーザーは rules で read 拒否されるため購読しない
  const enabled = Boolean(uid) && user?.level !== 'external' && canView;

  const [mineDocs, setMineDocs] = useState<AttentionDoc[]>([]);
  const [authorDocs, setAuthorDocs] = useState<AuthorDoc[]>([]);

  // 1. 宛先側: 自分が未対応宛先の投稿
  useEffect(() => {
    if (!enabled || !uid) {
      setMineDocs([]);
      return;
    }
    const q = query(
      collection(db, BOARD_COLLECTIONS.posts),
      where('openRecipientUids', 'array-contains', uid),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        // 家訓: ここでは setState のみ（Firestore 書き戻し禁止）
        setMineDocs(
          snap.docs.map((d) => {
            const data = d.data() as BoardPost;
            return {
              id: d.id,
              dueDate: data.dueDate ?? null,
              myState: data.recipientStates?.[uid]?.state ?? null,
              threadUnread: Math.max(
                0,
                (data.threadCount ?? 0) - (data.threadSeen?.[uid] ?? 0),
              ),
            };
          }),
        );
      },
      (err) => {
        console.warn('[useBoardAttention] 宛先側 snapshot error:', err);
        setMineDocs([]);
      },
    );
    return () => unsub();
  }, [enabled, uid]);

  // 2. 投稿者側: 自分の投稿（新着アクション件数 + スレッド新着の算出に使う）
  //    ※ authorUnseenActivity==true で絞ると スレッド新着だけある投稿が拾えないため、
  //      自分の投稿を購読して両方をクライアント側で数える。
  useEffect(() => {
    if (!enabled || !uid) {
      setAuthorDocs([]);
      return;
    }
    const q = query(
      collection(db, BOARD_COLLECTIONS.posts),
      where('authorUid', '==', uid),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        // 家訓: ここでは setState のみ（Firestore 書き戻し禁止）
        setAuthorDocs(
          snap.docs.map((d) => {
            const data = d.data() as BoardPost;
            return {
              id: d.id,
              unseenActivity: data.authorUnseenActivity === true,
              threadUnread: Math.max(
                0,
                (data.threadCount ?? 0) - (data.threadSeen?.[uid] ?? 0),
              ),
            };
          }),
        );
      },
      (err) => {
        console.warn('[useBoardAttention] 投稿者側 snapshot error:', err);
        setAuthorDocs([]);
      },
    );
    return () => unsub();
  }, [enabled, uid]);

  return useMemo(() => {
    if (!enabled) return ZERO;
    const today = todayYmd();
    const open = mineDocs.length;
    const unread = mineDocs.filter((d) => d.myState === 'unread').length;
    const overdue = mineDocs.filter((d) => d.dueDate != null && d.dueDate < today).length;
    const authorNews = authorDocs.filter((d) => d.unseenActivity).length;

    // スレッド新着: 宛先側と投稿者側で同じ投稿が両方に出る（自分宛かつ自分が投稿者）
    // ことがあるため、postId で重複排除してから合計する。
    const threadByPost = new Map<string, number>();
    for (const d of mineDocs) if (d.threadUnread > 0) threadByPost.set(d.id, d.threadUnread);
    for (const d of authorDocs) if (d.threadUnread > 0) threadByPost.set(d.id, d.threadUnread);
    let threadNews = 0;
    for (const n of threadByPost.values()) threadNews += n;

    return {
      unread,
      open,
      overdue,
      authorNews,
      badgeTotal: open + authorNews,
      threadNews,
      minePostIds: new Set(mineDocs.map((d) => d.id)),
    };
  }, [enabled, mineDocs, authorDocs]);
}
